import React from 'react';
import logo from './assets/logo.svg';

const FormsLandingPage = ({ onLoginClick, onDocsClick, branding }) => {
  const brandName = branding?.app_name || 'xavvy';
  const brandLogo = branding?.logo_url || logo;
  const brandFooterText = branding?.footer_text || `\u00a9 ${new Date().getFullYear()} ${brandName}. All rights reserved.`;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      {/* Header */}
      <header style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={brandLogo} alt={brandName} style={{ height: '32px', maxWidth: '120px', objectFit: 'contain' }} />
          <span style={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.5px' }}>{brandName}</span>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--accent-color)', background: 'rgba(99,102,241,0.1)', padding: '2px 10px', borderRadius: '999px', border: '1px solid rgba(99,102,241,0.2)' }}>Forms</span>
        </div>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <button onClick={onDocsClick} style={{ textDecoration: 'none', color: 'var(--text-secondary)', fontWeight: 500, padding: '8px 16px', borderRadius: '4px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
            onMouseOver={(e) => e.target.style.background = 'var(--bg-hover)'}
            onMouseOut={(e) => e.target.style.background = 'transparent'}
          >Documentation</button>
          <button onClick={onLoginClick} style={{ background: 'var(--accent-color)', color: 'white', border: 'none', padding: '8px 24px', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '1rem' }}
            onMouseDown={(e) => e.target.style.transform = 'scale(0.98)'}
            onMouseUp={(e) => e.target.style.transform = 'scale(1)'}
          >Login</button>
        </div>
      </header>

      <main style={{ flex: 1 }}>
        {/* Hero Section */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{
              display: 'inline-block',
              padding: '7px 16px',
              borderRadius: '20px',
              background: 'linear-gradient(90deg, rgba(99,102,241,0.12), rgba(168,85,247,0.12))',
              border: '1px solid var(--border-color)',
              marginBottom: '24px',
              fontSize: '0.9rem',
              color: 'var(--text-secondary)',
            }}>
              50+ field types &bull; Conditional logic &bull; Multi-step pages &bull; Analytics
            </div>
            <h1 style={{ fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: 800, lineHeight: 1.1, marginBottom: '20px', letterSpacing: '-1px' }}>
              Build Beautiful Forms<br />
              <span style={{ background: 'linear-gradient(90deg, var(--accent-color), #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                No Code Required
              </span>
            </h1>
            <p style={{ fontSize: '1.15rem', color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto 36px', lineHeight: 1.6 }}>
              Create surveys, applications, contact forms, and complex multi-step workflows with a powerful visual designer.
              Self-host for free or let us manage it.
            </p>
            <button onClick={onLoginClick} style={{
              background: 'linear-gradient(90deg, var(--accent-color), #a855f7)',
              color: 'white', border: 'none', padding: '14px 36px', borderRadius: '8px',
              fontWeight: 600, fontSize: '1.1rem', cursor: 'pointer', transition: 'transform 0.1s, opacity 0.15s',
            }}
              onMouseDown={(e) => e.target.style.transform = 'scale(0.97)'}
              onMouseUp={(e) => e.target.style.transform = 'scale(1)'}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              Start Building Forms
            </button>
          </div>
        </section>

        {/* Features Grid */}
        <section style={{ padding: '80px 20px', background: 'var(--bg-primary)' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '14px' }}>
              <span style={{ display: 'inline-block', padding: '5px 12px', borderRadius: '999px', fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-color)', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                Capabilities
              </span>
            </div>
            <h2 style={{ textAlign: 'center', fontSize: '2.2rem', fontWeight: 700, marginBottom: '48px' }}>
              Everything you need to collect data
            </h2>

            {/* Hero feature cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '48px' }}>
              <FeatureCard icon="🎨" title="Visual Form Designer" description="Drag-and-drop builder with 50+ field types: text, email, rating scales, NPS, signatures, file uploads, address fields, matrices, and more." />
              <FeatureCard icon="🔀" title="Conditional Logic" description="Show, hide, or require fields based on user responses. Create dynamic forms that adapt to each respondent in real time." />
              <FeatureCard icon="📊" title="Submission Analytics" description="Track conversion funnels, field abandonment, and completion times. Understand where respondents drop off and optimize your forms." />
            </div>

            {/* Compact capability categories */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <CompactCategory name="Field Types" accent="#6366f1" items={[
                ['A', 'Text, Email, Phone, Password'],
                ['#', 'Number, Currency, Slider'],
                ['*', 'Rating, NPS, Opinion Scale'],
                ['V', 'Select, Radio, Checkbox'],
                ['F', 'File Upload, Image Upload'],
                ['S', 'Signature, Color Picker'],
                ['M', 'Address, Map, Lookup'],
              ]} />
              <CompactCategory name="Layout & Pages" accent="#10b981" items={[
                ['P', 'Multi-Step Pages'],
                ['C', 'Columns & Tabs'],
                ['R', 'Repeating Groups'],
                ['S', 'Collapsible Sections'],
                ['B', 'Progress Bars'],
                ['D', 'HTML, Images, Video'],
              ]} />
              <CompactCategory name="Theming" accent="#f59e0b" items={[
                ['C', 'Custom Colors & Fonts'],
                ['L', 'Card & Inline Layouts'],
                ['R', 'Border Radius Control'],
                ['S', 'Custom CSS Support'],
                ['P', 'URL-Based Prefill'],
                ['A', 'Auto-Save Drafts'],
              ]} />
              <CompactCategory name="Distribution" accent="#ef4444" items={[
                ['L', 'Public Shareable URLs'],
                ['E', 'Embed Code (Script Tag)'],
                ['H', 'Honeypot Spam Protection'],
                ['P', 'Password Protection'],
                ['G', 'Version Control (Git Sync)'],
              ]} />
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section style={{ padding: '80px 20px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '14px' }}>
              <span style={{ display: 'inline-block', padding: '5px 12px', borderRadius: '999px', fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-color)', border: '1px solid var(--border-color)', background: 'var(--bg-primary)' }}>
                Pricing
              </span>
            </div>
            <h2 style={{ textAlign: 'center', fontSize: '2.2rem', fontWeight: 700, marginBottom: '12px' }}>
              Simple, transparent pricing
            </h2>
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '48px', fontSize: '1.05rem' }}>
              Self-host for free forever, or let us manage it for you.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', alignItems: 'stretch' }}>
              <PricingCard
                name="Community"
                price="Free"
                period="forever"
                description="The full form builder, open-source and self-hosted."
                highlight={false}
                cta="Get Started"
                onCta={onLoginClick}
                features={[
                  '5 forms, 1,000 submissions/month',
                  '50+ field types',
                  'Conditional logic & multi-step',
                  'Public URLs & embed code',
                  'Submission management',
                  'Custom theming',
                  'Community support (GitHub)',
                ]}
              />
              <PricingCard
                name="Cloud"
                price="$5"
                period="per seat / month"
                description="Unlimited forms hosted and managed for you."
                highlight={true}
                cta="Start Free Trial"
                onCta={onLoginClick}
                features={[
                  'Unlimited forms & submissions',
                  'AI-powered form builder',
                  'Advanced analytics & funnels',
                  'Password protection',
                  'Git sync for version control',
                  'Priority email support',
                ]}
              />
              <PricingCard
                name="Enterprise"
                price="Custom"
                period="annual contract"
                description="For teams needing white-label or compliance."
                highlight={false}
                cta="Contact Sales"
                onCta={onLoginClick}
                features={[
                  'Everything in Cloud',
                  'White-label & custom domain',
                  'SSO / SAML / LDAP',
                  'Dedicated infrastructure',
                  'SLA with guaranteed response',
                  'Dedicated customer success',
                ]}
              />
            </div>
          </div>
        </section>

        {/* Upsell Section */}
        <section style={{
          padding: '60px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
          borderTop: '1px solid var(--border-color)',
        }}>
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: '16px' }}>
              Need workflow automation?
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.05rem', lineHeight: 1.6, marginBottom: '28px' }}>
              <strong>{brandName} Flow</strong> connects your forms to 140+ integrations.
              Trigger Slack messages, create CRM records, send emails, and run AI agents — all from a single form submission.
            </p>
            <button onClick={onLoginClick} style={{
              background: 'transparent', color: 'var(--accent-color)',
              border: '2px solid var(--accent-color)', padding: '12px 32px',
              borderRadius: '8px', fontWeight: 600, fontSize: '1rem', cursor: 'pointer',
              transition: 'background 0.15s, color 0.15s',
            }}
              onMouseOver={(e) => { e.currentTarget.style.background = 'var(--accent-color)'; e.currentTarget.style.color = 'white'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--accent-color)'; }}
            >
              Learn about {brandName} Flow
            </button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer style={{
        padding: '30px 20px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
        borderTop: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)',
        fontSize: '0.9rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '12px' }}>
          <a href="/privacy" style={{ color: 'var(--text-tertiary)', textDecoration: 'none' }}>Privacy Policy</a>
          <a href="/terms" style={{ color: 'var(--text-tertiary)', textDecoration: 'none' }}>Terms of Service</a>
        </div>
        <p>{brandFooterText}</p>
      </footer>
    </div>
  );
};

/* Reusable sub-components (same patterns as LandingPage) */

const FeatureCard = ({ icon, title, description }) => (
  <div style={{
    background: 'var(--bg-secondary)',
    padding: '30px',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'default'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-5px)';
    e.currentTarget.style.boxShadow = 'var(--shadow-strong)';
    e.currentTarget.style.borderColor = 'var(--accent-color)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.borderColor = 'var(--border-color)';
  }}>
    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>{icon}</div>
    <h3 style={{ fontSize: '1.5rem', marginBottom: '10px', color: 'var(--text-primary)' }}>{title}</h3>
    <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{description}</p>
  </div>
);

const CompactCategory = ({ name, accent, items }) => (
  <div style={{
    background: 'var(--bg-secondary)',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    padding: '24px',
    transition: 'border-color 0.2s',
  }}
  onMouseEnter={(e) => e.currentTarget.style.borderColor = accent}
  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
  >
    <h4 style={{
      fontSize: '0.85rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: accent,
      marginBottom: '16px',
      paddingBottom: '10px',
      borderBottom: `2px solid ${accent}22`,
    }}>{name}</h4>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {items.map(([icon, title]) => (
        <div key={title} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          <span style={{ fontSize: '0.95rem', flexShrink: 0, width: '20px', textAlign: 'center' }}>{icon}</span>
          <span>{title}</span>
        </div>
      ))}
    </div>
  </div>
);

const PricingCard = ({ name, price, period, description, highlight, cta, onCta, features }) => (
  <div style={{
    background: highlight ? 'linear-gradient(160deg, rgba(99,102,241,0.12) 0%, rgba(168,85,247,0.10) 100%)' : 'var(--bg-primary)',
    border: highlight ? '2px solid var(--accent-color)' : '1px solid var(--border-color)',
    borderRadius: '16px',
    padding: '36px 32px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  }}>
    {highlight && (
      <div style={{
        position: 'absolute', top: '16px', right: '16px',
        background: 'linear-gradient(90deg, var(--accent-color), #a855f7)',
        color: 'white', fontSize: '0.72rem', fontWeight: 700,
        padding: '3px 10px', borderRadius: '999px', letterSpacing: '0.05em', textTransform: 'uppercase'
      }}>Most Popular</div>
    )}
    <div style={{ marginBottom: '8px', fontWeight: 700, fontSize: '1rem', color: 'var(--accent-color)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{name}</div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '4px' }}>
      <span style={{ fontSize: '2.8rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{price}</span>
      {price !== 'Custom' && <span style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>/{period}</span>}
    </div>
    {price === 'Custom' && <div style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '4px' }}>{period}</div>}
    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.5, marginBottom: '28px', marginTop: '8px' }}>{description}</p>
    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0', flex: 1 }}>
      {features.map(f => (
        <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px', fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
          <span style={{ color: '#10b981', fontWeight: 700, flexShrink: 0, marginTop: '1px' }}>&#10003;</span>
          {f}
        </li>
      ))}
    </ul>
    <button onClick={onCta} style={{
      background: highlight ? 'linear-gradient(90deg, var(--accent-color), #a855f7)' : 'transparent',
      color: highlight ? 'white' : 'var(--text-primary)',
      border: highlight ? 'none' : '1px solid var(--border-color)',
      borderRadius: '8px', padding: '13px 0', fontWeight: 600, fontSize: '1rem',
      cursor: 'pointer', width: '100%', transition: 'opacity 0.15s',
    }}
      onMouseOver={(e) => e.currentTarget.style.opacity = '0.85'}
      onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
    >{cta}</button>
  </div>
);

export default FormsLandingPage;
